import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MapPin, CheckCircle, Phone, ArrowRight, ChevronDown, ChevronUp, Building2, Factory, Warehouse } from "lucide-react";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const cityData = {
  "noida": {
    name: "Noida",
    fullName: "Noida & Greater Noida",
    region: "Delhi NCR",
    title: "Scaffolding Rental in Noida - Quick Delivery | Amar Preet",
    metaDescription: "Leading scaffolding rental provider in Noida & Greater Noida. Cuplock, props, industrial scaffolding. Same-day quotes, fast mobilization.",
    h1: "Scaffolding Rental in Noida & Greater Noida",
    heroImage: "/images/assets/hero-scaffolding.jpeg",
    intro: "Noida has emerged as one of India's fastest-growing construction hubs, with numerous high-rise residential towers, commercial complexes, and institutional buildings under development. Amar Preet Scaffolding provides comprehensive scaffolding rental solutions across Noida and Greater Noida with local inventory for quick mobilization.",
    areas: ["Sector 62, 63", "Greater Noida West", "Noida Extension", "Yamuna Expressway", "Knowledge Park", "Tech Zone"],
    projects: ["Galaxy Blue Sapphire Plaza", "Embassy Oxygen Business Park", "Ajnara Panorama"],
    projectTypes: ["High-rise residential towers", "IT parks and commercial complexes", "Educational institutions", "Healthcare facilities"],
    stats: { projects: 45, clients: 60 }
  },
  "delhi-ncr": {
    name: "Delhi NCR",
    fullName: "Delhi NCR Region",
    region: "National Capital Region",
    title: "Scaffolding Rental Delhi NCR - Trusted Provider | Amar Preet",
    metaDescription: "Premium scaffolding rental services across Delhi NCR. 30+ years experience, ISO certified. Fast delivery to Delhi, Noida, Faridabad.",
    h1: "Scaffolding Rental in Delhi NCR",
    heroImage: "/images/assets/hero-scaffolding.jpeg",
    intro: "As our headquarters and primary service area, Delhi NCR receives our best-in-class scaffolding solutions. With 30+ years of experience serving the National Capital Region, we understand the unique requirements of construction projects in this dynamic market.",
    areas: ["Central Delhi", "South Delhi", "East Delhi", "Dwarka", "Rohini", "Faridabad"],
    projects: ["DIAL Phase 3A - Delhi Airport", "Vegas Mall Dwarka"],
    projectTypes: ["Airport infrastructure", "Metro projects", "Commercial malls", "Government buildings"],
    stats: { projects: 80, clients: 120 }
  },
  "hyderabad": {
    name: "Hyderabad",
    fullName: "Hyderabad & Secunderabad",
    region: "Telangana",
    title: "Scaffolding Rental Hyderabad - Trusted by EPCs | Amar Preet",
    metaDescription: "Hyderabad's preferred scaffolding rental partner. 30+ years experience, 100+ projects. Cuplock, shuttering, industrial scaffolding. Free site survey.",
    h1: "Scaffolding Rental in Hyderabad",
    heroImage: "/images/assets/hero-scaffolding.jpeg",
    intro: "Hyderabad's construction sector continues to boom with major residential, commercial, and infrastructure projects across the city. From HITEC City's IT corridors to the residential hubs of Gachibowli and Financial District, Amar Preet provides trusted scaffolding solutions.",
    areas: ["HITEC City", "Madhapur", "Gachibowli", "Financial District", "Banjara Hills", "Jubilee Hills", "Kukatpally", "Kompally"],
    projects: ["My Home Bhooja", "My Home Villas", "Trendset Jayabheri Elevate", "Police Commissionerate"],
    projectTypes: ["High-rise residential", "IT parks", "Government projects", "Commercial complexes"],
    stats: { projects: 50, clients: 70 }
  },
  "vizag": {
    name: "Vizag",
    fullName: "Visakhapatnam",
    region: "Andhra Pradesh",
    title: "Scaffolding Rental Vizag - Industrial & Commercial | Amar Preet",
    metaDescription: "Professional scaffolding rental in Visakhapatnam for industrial plants, refineries, and commercial projects. Quick mobilization. Expert support.",
    h1: "Scaffolding Rental in Vizag (Visakhapatnam)",
    heroImage: "/images/assets/hero-scaffolding.jpeg",
    intro: "Visakhapatnam is a major industrial hub on India's east coast, home to refineries, steel plants, shipyards, and pharmaceutical facilities. Our industrial scaffolding solutions meet the stringent safety and compliance requirements of these critical sectors.",
    areas: ["Steel Plant Area", "Refinery Zone", "Pharma City", "Port Area", "Gajuwaka", "MVP Colony"],
    projects: ["Tata Chemicals Limited", "Industrial plant maintenance projects"],
    projectTypes: ["Refinery maintenance", "Steel plant projects", "Shipyard construction", "Pharmaceutical facilities"],
    stats: { projects: 25, clients: 30 }
  }
};

export default function CityLandingPage({ city: cityProp }) {
  const city = cityProp;
  const data = cityData[city];
  const [projects, setProjects] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    if (data) {
      document.title = data.title;
    }
    
    axios.get(`${API}/projects`)
      .then(res => {
        const cityProjects = res.data.filter(p => 
          p.location.toLowerCase().includes(data?.name.toLowerCase()) ||
          data?.projects.some(dp => p.name.includes(dp.split(' ')[0]))
        );
        setProjects(cityProjects.slice(0, 3));
      })
      .catch(console.error);
  }, [city, data]);

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">City Not Found</h1>
          <Link to="/" className="text-yellow-600 hover:underline">Back to Home</Link>
        </div>
      </div>
    );
  }

  const faqs = [
    {
      question: `What scaffolding solutions do you offer in ${data.name}?`,
      answer: `We offer comprehensive scaffolding solutions in ${data.name} including Cuplock systems, MS pipes & tubes, steel plates & planks, couplers & joints, adjustable props & jacks, and Acrospan formwork systems.`
    },
    {
      question: `How quickly can you deliver scaffolding in ${data.name}?`,
      answer: `With our inventory network, we typically mobilize within 24-48 hours for ${data.name} area projects. For large-scale requirements, we recommend advance booking.`
    },
    {
      question: `Do you provide installation support in ${data.name}?`,
      answer: `We do not provide installation support. We only supply the scaffolding material on rent in ${data.name}. Your team or contractor handles the erection and dismantling on site.`
    },
    {
      question: `What is the minimum rental period in ${data.name}?`,
      answer: `The minimum rental period is 3 months. We offer competitive pricing for long-term rentals in ${data.name}.`
    }
  ];

  return (
    <div data-testid={`city-page-${city}`}>
      <SEO title={`Scaffolding Rental in ${data.name}`} description={data.metaDescription} canonical={`/scaffolding-rental-${city}`} />
      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white overflow-hidden" style={{ minHeight: "60vh" }}>
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url('${data.heroImage}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/90" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 flex items-center" style={{ minHeight: "60vh" }}>
          <div className="max-w-3xl">
            <nav className="mb-6 text-slate-400 text-sm">
              <Link to="/" className="hover:text-yellow-400">Home</Link>
              <span className="mx-2">/</span>
              <Link to="/services" className="hover:text-yellow-400">Services</Link>
              <span className="mx-2">/</span>
              <span className="text-yellow-400">{data.name}</span>
            </nav>

            <div className="inline-flex items-center px-4 py-2 bg-yellow-500/20 backdrop-blur-sm border border-yellow-500/30 rounded-full text-yellow-300 text-sm font-medium mb-6">
              <MapPin className="w-4 h-4 mr-2" />
              {data.region}
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-testid="city-headline">
              {data.h1}
            </h1>

            <p className="text-xl text-slate-300 leading-relaxed mb-8">
              {data.intro}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="bg-yellow-500 hover:bg-yellow-600 text-slate-900 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:scale-105 inline-flex items-center justify-center"
                data-testid="city-cta-quote"
              >
                Get {data.name} Quote
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <a
                href="tel:+919355744699"
                className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-yellow-500 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <p className="text-3xl font-bold text-slate-900">{data.stats.projects}+</p>
              <p className="text-sm text-slate-700">Projects in {data.name}</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-900">{data.stats.clients}+</p>
              <p className="text-sm text-slate-700">Local Clients</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-900">30+</p>
              <p className="text-sm text-slate-700">Years Experience</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-900">24-48h</p>
              <p className="text-sm text-slate-700">Delivery Time</p>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Served */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-yellow-500 font-semibold text-sm tracking-wider uppercase">Coverage Area</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-4 mb-6">
                Areas We Serve in {data.fullName}
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                Our scaffolding rental services cover all major construction zones across {data.fullName}.
                {city === "delhi-ncr" && " We also serve major Northern India states like UP, Punjab, Haryana, Uttarakhand, Rajasthan, Madhya Pradesh and Kolkata from our Delhi NCR branch."}
                {city === "hyderabad" && " We also serve major South Indian states like Telangana, Andhra Pradesh, Karnataka, Tamil Nadu, Odisha, Maharashtra etc. from our Hyderabad branch."}
                {city === "noida" && " We also serve major Northern India states like UP, Punjab, Haryana, Uttarakhand, Rajasthan, Madhya Pradesh and Kolkata from our Noida branch."} With strategic inventory placement, we ensure quick delivery to your project site.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {data.areas.map((area) => (
                  <div key={area} className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                    <span className="text-slate-700">{area}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-50 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-slate-900 mb-6">
                Project Types in {data.name}
              </h3>
              <ul className="space-y-4">
                {data.projectTypes.map((type) => (
                  <li key={type} className="flex items-center space-x-3 text-slate-600">
                    <ArrowRight className="w-4 h-4 text-yellow-500" />
                    <span>{type}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services Available */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Scaffolding Services in {data.name}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Complete range of scaffolding and shuttering solutions available for {data.name} projects
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: "Cuplock Scaffolding", slug: "cuplock-scaffolding", image: "/images/products/cuplock.png" },
              { name: "Shuttering Material", slug: "shuttering-material-on-rent", image: "/images/products/steel-plates.png" },
              { name: "Industrial Scaffolding", slug: "industrial-scaffolding", image: "/images/products/ms-pipes.png" },
            ].map((service) => (
              <Link
                key={service.slug}
                to={`/${service.slug}`}
                className="group bg-white rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-transparent hover:border-yellow-500"
              >
                <div className="w-20 h-20 bg-yellow-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform overflow-hidden">
                  <img src={service.image} alt={service.name} className="w-full h-full object-contain invert mix-blend-multiply" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{service.name}</h3>
                <p className="text-slate-600 text-sm mb-4">Available for all {data.name} projects</p>
                <span className="inline-flex items-center text-sm font-semibold text-yellow-600 group-hover:text-yellow-700">
                  Learn More <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Why Choose Amar Preet in {data.name}?
              </h2>
              <div className="space-y-6">
                {[
                  { title: "Local Inventory", desc: `Dedicated stock for ${data.name} ensuring quick mobilization` },
                  { title: "Experienced Team", desc: "30+ years of expertise in scaffolding solutions" },
                  { title: "Quality Certified", desc: "ISO 9001:2015 certified equipment and processes" },
                  { title: "Material Supply", desc: "Quality scaffolding and shuttering material on rent" },
                  { title: "Competitive Pricing", desc: "Best rates with flexible rental terms" }
                ].map((item) => (
                  <div key={item.title} className="flex space-x-4">
                    <CheckCircle className="w-6 h-6 text-yellow-500 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-white mb-1">{item.title}</h4>
                      <p className="text-slate-400 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-yellow-500 rounded-2xl p-8 text-center">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                Get a Quote for Your {data.name} Project
              </h3>
              <p className="text-slate-700 mb-6">
                Free site survey and quantity estimation
              </p>
              <Link
                to="/contact"
                className="inline-block bg-slate-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-slate-800 transition-all"
              >
                Request Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
              FAQs - Scaffolding Rental in {data.name}
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-slate-50 rounded-xl overflow-hidden" data-testid={`faq-${index}`}>
                <button
                  className="w-full px-6 py-5 flex items-center justify-between text-left"
                  onClick={() => setOpenFaq(openFaq === index ? -1 : index)}
                >
                  <span className="font-semibold text-slate-900 pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5">
                    <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Cities */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-xl font-bold text-slate-900 mb-6 text-center">Also Serving</h3>
          <div className="flex flex-wrap justify-center gap-4">
            {Object.entries(cityData).filter(([key]) => key !== city).map(([key, value]) => (
              <Link
                key={key}
                to={`/scaffolding-rental-${key}`}
                className="px-6 py-3 bg-white border border-slate-200 rounded-lg hover:border-yellow-500 hover:shadow-lg transition-all"
              >
                {value.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
